/**
 * LineSprintLeaderboard — log times for 8/16/32 sets and view fastest athletes.
 * Mounts inside the Line Sprints drill detail. Can be navigated to standalone.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Trophy, Clock, Star, Loader2, X, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const METRICS = [
  { key: "total", label: "Total (8+16+32)" },
  { key: "set_1", label: "Set 1 · 8 Lines" },
  { key: "set_2", label: "Set 2 · 16 Lines" },
  { key: "set_3", label: "Set 3 · 32 Lines" },
];

const PERIODS = [
  { key: "weekly", label: "Week" },
  { key: "monthly", label: "Month" },
  { key: "all", label: "All Time" },
];

const formatTime = (s) => {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  const sec = (s - m * 60).toFixed(1);
  return m > 0 ? `${m}:${sec.padStart(4, "0")}` : `${sec}s`;
};

const LineSprintLeaderboard = () => {
  const [metric, setMetric] = useState("total");
  const [period, setPeriod] = useState("weekly");
  const [board, setBoard] = useState(null);
  const [prs, setPrs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogger, setShowLogger] = useState(false);
  const [form, setForm] = useState({ set_1_seconds: "", set_2_seconds: "", set_3_seconds: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [lastPR, setLastPR] = useState(null);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [bd, hist] = await Promise.all([
        axios.get(`${API}/line-sprints/leaderboard?metric=${metric}&period=${period}&limit=10`, { headers: headers() }),
        axios.get(`${API}/line-sprints/history?limit=5`, { headers: headers() }),
      ]);
      setBoard(bd.data);
      setPrs(hist.data?.personal_bests || {});
    } catch (e) {
      console.error("line-sprints load failed", e);
    } finally {
      setLoading(false);
    }
  }, [metric, period]);

  // Initial + filter-change data load. setState inside fetchAll is intentional.
  // eslint-disable-next-line
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const submitTimes = useCallback(() => {
    const payload = {
      set_1_seconds: parseFloat(form.set_1_seconds),
      set_2_seconds: parseFloat(form.set_2_seconds),
      set_3_seconds: parseFloat(form.set_3_seconds),
      notes: form.notes || undefined,
    };
    if (!payload.set_1_seconds || !payload.set_2_seconds || !payload.set_3_seconds) return;
    setSubmitting(true);
    axios
      .post(`${API}/line-sprints/log`, payload, { headers: headers() })
      .then((res) => {
        setLastPR(res.data.is_pr);
        setForm({ set_1_seconds: "", set_2_seconds: "", set_3_seconds: "", notes: "" });
        window.setTimeout(() => {
          setShowLogger(false);
          setLastPR(null);
        }, 3000);
        return fetchAll();
      })
      .catch((err) => console.error("submit failed", err))
      .finally(() => setSubmitting(false));
  }, [form, fetchAll]);

  const onSubmit = (e) => {
    e.preventDefault();
    submitTimes();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#0A0A0A] border border-white/[0.06] p-5 rounded-sm space-y-4"
      data-testid="line-sprint-leaderboard"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Trophy className="w-3.5 h-3.5 text-amber-400" />
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-400">
              Line Sprints · Leaderboard
            </p>
          </div>
          <h3 className="text-xl font-black uppercase text-white">8 / 16 / 32 for Time</h3>
        </div>
        <button
          onClick={() => setShowLogger((v) => !v)}
          className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-black uppercase tracking-wider px-3 py-2 inline-flex items-center gap-1.5"
          data-testid="open-log-times-btn"
        >
          {showLogger ? <X className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
          {showLogger ? "Close" : "Log Times"}
        </button>
      </div>

      {/* Personal Bests Strip */}
      {prs && Object.values(prs).some((v) => v != null) && (
        <div className="grid grid-cols-4 gap-px bg-white/[0.06]" data-testid="my-prs">
          {["set_1", "set_2", "set_3", "total"].map((k) => (
            <div key={k} className="bg-[#0A0A0A] p-2 text-center">
              <p className="text-[9px] font-mono uppercase tracking-widest text-white/40">
                {k === "set_1" ? "8L PR" : k === "set_2" ? "16L PR" : k === "set_3" ? "32L PR" : "Total PR"}
              </p>
              <p className="text-sm font-black text-white mt-0.5">{formatTime(prs[k])}</p>
            </div>
          ))}
        </div>
      )}

      {/* Logger form */}
      {showLogger && (
        <form
          onSubmit={onSubmit}
          className="bg-black/40 border border-orange-500/30 p-4 rounded space-y-3"
          data-testid="log-times-form"
        >
          {lastPR && (
            <div className="flex items-center gap-2 text-emerald-400 text-xs font-bold uppercase tracking-wider" data-testid="pr-banner">
              <CheckCircle2 className="w-4 h-4" />
              Logged!{" "}
              {Object.entries(lastPR).filter(([, v]) => v).length > 0 && (
                <>New PR on{" "}
                  {Object.entries(lastPR).filter(([, v]) => v).map(([k]) => k.replace("_", " ")).join(", ")}
                  <Star className="w-4 h-4 ml-1 text-amber-400 fill-amber-400" />
                </>
              )}
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3].map((n) => (
              <div key={n}>
                <label className="text-[10px] font-mono uppercase tracking-widest text-white/50">
                  Set {n} · {n === 1 ? "8" : n === 2 ? "16" : "32"}L
                </label>
                <input
                  type="number"
                  step="0.1"
                  required
                  placeholder="seconds"
                  value={form[`set_${n}_seconds`]}
                  onChange={(e) => setForm({ ...form, [`set_${n}_seconds`]: e.target.value })}
                  className="w-full mt-1 h-9 px-2 bg-zinc-900 border border-white/[0.08] text-white text-sm focus:outline-none focus:border-orange-400"
                  data-testid={`log-set-${n}-input`}
                />
              </div>
            ))}
          </div>
          <input
            type="text"
            placeholder="Notes (optional)"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="w-full h-9 px-2 bg-zinc-900 border border-white/[0.08] text-white text-xs focus:outline-none focus:border-orange-400"
            data-testid="log-notes-input"
            maxLength={200}
          />
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-black text-xs uppercase tracking-wider py-2.5 inline-flex items-center justify-center gap-2"
            data-testid="submit-log-btn"
          >
            {submitting ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</> : "Save Times"}
          </button>
        </form>
      )}

      {/* Metric + period filters */}
      <div className="space-y-2">
        <div className="flex gap-px bg-white/[0.06] flex-wrap">
          {METRICS.map((m) => (
            <button
              key={m.key}
              onClick={() => setMetric(m.key)}
              className={`flex-1 min-w-[100px] py-2 px-3 text-[10px] font-mono uppercase tracking-widest ${
                metric === m.key ? "bg-orange-500 text-white" : "bg-[#0A0A0A] text-white/50 hover:text-white"
              }`}
              data-testid={`metric-${m.key}-btn`}
            >
              {m.label}
            </button>
          ))}
        </div>
        <div className="flex gap-px bg-white/[0.06]">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`flex-1 py-1.5 text-[10px] font-mono uppercase tracking-widest ${
                period === p.key ? "bg-amber-500/30 text-amber-300" : "bg-[#0A0A0A] text-white/40 hover:text-white"
              }`}
              data-testid={`period-${p.key}-btn`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Leaderboard */}
      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="w-5 h-5 text-orange-400 animate-spin" />
        </div>
      ) : board && board.rankings.length > 0 ? (
        <div className="space-y-px bg-white/[0.06]">
          {board.rankings.map((r) => (
            <div
              key={r.user_id}
              className={`flex items-center justify-between p-2.5 ${
                r.is_me ? "bg-orange-500/10 border-l-2 border-orange-500" : "bg-[#0A0A0A]"
              }`}
              data-testid={`leaderboard-rank-${r.rank}`}
            >
              <div className="flex items-center gap-3">
                <span
                  className={`text-base font-black w-6 text-center ${
                    r.rank === 1 ? "text-amber-400" : r.rank === 2 ? "text-zinc-300" : r.rank === 3 ? "text-orange-700" : "text-white/30"
                  }`}
                >
                  {r.rank}
                </span>
                <span className="text-sm text-white">
                  {r.name}{r.is_me && <span className="text-[10px] text-orange-400 ml-1">(you)</span>}
                </span>
              </div>
              <span className="text-sm font-mono font-black text-white">
                {formatTime(r.best_time_seconds)}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-xs text-white/40 font-mono uppercase tracking-widest">
          No times logged yet · be the first
        </div>
      )}

      {board?.my_rank && !board.rankings.some((r) => r.is_me) && (
        <div className="text-xs text-white/50 text-center pt-2 border-t border-white/[0.06]">
          You&apos;re rank #{board.my_rank.rank} · best {formatTime(board.my_rank.best_time_seconds)}
        </div>
      )}
    </motion.div>
  );
};

export default LineSprintLeaderboard;
